/*
LIME LABS COMMERCE LAYER
Component: Lifestyle Edits Motion Engine
Project: La Troupe
Version: 4.0
Status: LLCL-DRAFT

ARCHITECTURE
============================================================

DESKTOP
- Existing vertical stacked-card behavior
- Vertical page scroll
- Sticky stage
- Cards translate Y: 108% -> 0%
- Existing desktop composition preserved

MOBILE
- Dedicated independent engine
- Vertical page scroll remains native
- Dedicated 100svh sticky viewport
- Cards enter horizontally from RIGHT
- Cards translate X: 104% -> 0%
- Previous card receives only subtle depth movement
- No horizontal user scroll
- No scroll hijacking
- No touch interception

PERFORMANCE
============================================================
- requestAnimationFrame
- transform only during scroll
- no external libraries
- ResizeObserver
- reduced-motion support
*/


(() => {
  'use strict';


  /* ========================================================
     GLOBAL SINGLETON
     Prevent duplicate initialization when Shopify renders
     the same asset more than once.
     ======================================================== */

  const GLOBAL_KEY = 'LLCLLifestyleEditsV4';


  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].scheduleInit();
    return;
  }


  const controllers = new Set();
  const controllerMap = new WeakMap();


  /* ========================================================
     UTILITIES
     ======================================================== */

  const clamp = (
    value,
    min = 0,
    max = 1
  ) => {
    return Math.min(
      max,
      Math.max(
        min,
        value
      )
    );
  };


  const padNumber = value => {
    return String(value).padStart(
      2,
      '0'
    );
  };


  const editorialEase = value => {
    const x = clamp(value);

    return (
      1 -
      Math.pow(
        1 - x,
        3
      )
    );
  };


  const absoluteTop = element => {
    return (
      element.getBoundingClientRect().top +
      window.scrollY
    );
  };


  const supportsInert = (
    typeof HTMLElement !== 'undefined' &&
    'inert' in HTMLElement.prototype
  );


  /* ========================================================
     CONTROLLER
     One controller per Lifestyle Edits section.
     ======================================================== */

  class LifestyleEditsController {

    constructor(root) {

      this.root = root;

      this.sectionId =
        root.id.replace(
          /^LLLifestyle-/,
          ''
        );


      /* ----------------------------------------------------
         ENGINES
         ---------------------------------------------------- */

      this.desktopEngine =
        root.querySelector(
          '[data-ll-desktop]'
        );

      this.desktopTrack =
        root.querySelector(
          '[data-ll-desktop-track]'
        );

      this.desktopStage =
        root.querySelector(
          '[data-ll-desktop-stage]'
        );

      this.desktopScenes =
        Array.from(
          root.querySelectorAll(
            '[data-ll-desktop-scene]'
          )
        );

      this.desktopIndexItems =
        Array.from(
          root.querySelectorAll(
            '[data-ll-desktop-index]'
          )
        );

      this.desktopCounter =
        root.querySelector(
          '[data-ll-desktop-counter]'
        );


      this.mobileEngine =
        root.querySelector(
          '[data-ll-mobile]'
        );

      this.mobileTrack =
        root.querySelector(
          '[data-ll-mobile-track]'
        );

      this.mobileStage =
        root.querySelector(
          '[data-ll-mobile-stage]'
        );

      this.mobileScenes =
        Array.from(
          root.querySelectorAll(
            '[data-ll-mobile-scene]'
          )
        );


      /* ----------------------------------------------------
         VALIDATION
         ---------------------------------------------------- */

      this.valid = Boolean(
        this.desktopEngine &&
        this.desktopTrack &&
        this.desktopStage &&
        this.mobileEngine &&
        this.mobileTrack &&
        this.mobileStage &&
        this.desktopScenes.length
      );


      if (!this.valid) {
        return;
      }


      this.total =
        this.desktopScenes.length;


      /* ----------------------------------------------------
         MOTION
         ---------------------------------------------------- */

      this.reducedMotion =
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        );


      this.mode = null;

      this.activeDesktop = -1;
      this.activeMobile = -1;

      this.renderTicking = false;
      this.geometryTicking = false;

      this.destroyed = false;


      /* ----------------------------------------------------
         BOUND METHODS
         ---------------------------------------------------- */

      this.onScroll =
        this.onScroll.bind(this);

      this.onResize =
        this.onResize.bind(this);

      this.onPageShow =
        this.onPageShow.bind(this);

      this.onBlockSelect =
        this.onBlockSelect.bind(this);

      this.onMotionPreference =
        this.onMotionPreference.bind(this);


      /* ----------------------------------------------------
         START
         ---------------------------------------------------- */

      this.bindEvents();

      this.applyMode(true);

      this.setGeometry();

      this.requestRender();


      /*
      Important:
      Shopify/theme CSS and fonts may finish applying after
      the asset executes. Re-measure after layout settles.
      */

      window.requestAnimationFrame(
        () => {

          window.requestAnimationFrame(
            () => {

              if (this.destroyed) {
                return;
              }

              this.setGeometry();
              this.requestRender();

            }
          );

        }
      );


      window.setTimeout(
        () => {

          if (this.destroyed) {
            return;
          }

          this.setGeometry();
          this.requestRender();

        },
        180
      );

    }


    /* ======================================================
       ROOT CSS NUMBER
       ====================================================== */

    getRootNumber(
      property,
      fallback = 0
    ) {

      const raw =
        getComputedStyle(this.root)
          .getPropertyValue(property);

      const parsed =
        parseFloat(raw);


      return Number.isFinite(parsed)
        ? parsed
        : fallback;

    }


    /* ======================================================
       MODE
       ====================================================== */

    getCurrentMode() {

      return (
        window.innerWidth <= 899
          ? 'mobile'
          : 'desktop'
      );

    }


    applyMode(force = false) {

      const nextMode =
        this.getCurrentMode();


      if (
        !force &&
        nextMode === this.mode
      ) {
        return;
      }


      this.mode =
        nextMode;


      const desktopActive =
        nextMode === 'desktop';


      this.desktopEngine.setAttribute(
        'aria-hidden',
        desktopActive
          ? 'false'
          : 'true'
      );


      this.mobileEngine.setAttribute(
        'aria-hidden',
        desktopActive
          ? 'true'
          : 'false'
      );


      if (supportsInert) {

        this.desktopEngine.inert =
          !desktopActive;

        this.mobileEngine.inert =
          desktopActive;

      }


      /*
      Re-assert scene interaction after changing engines.
      */

      if (desktopActive) {

        this.setDesktopActive(
          Math.max(
            this.activeDesktop,
            0
          ),
          true
        );

      } else {

        this.setMobileActive(
          Math.max(
            this.activeMobile,
            0
          ),
          true
        );

      }


      this.setGeometry();
      this.requestRender();

    }


    /* ======================================================
       EVENTS
       ====================================================== */

    bindEvents() {

      window.addEventListener(
        'scroll',
        this.onScroll,
        {
          passive: true
        }
      );


      window.addEventListener(
        'resize',
        this.onResize,
        {
          passive: true
        }
      );


      window.addEventListener(
        'orientationchange',
        this.onResize,
        {
          passive: true
        }
      );


      window.addEventListener(
        'pageshow',
        this.onPageShow
      );


      document.addEventListener(
        'shopify:block:select',
        this.onBlockSelect
      );


      if (
        typeof this.reducedMotion
          .addEventListener === 'function'
      ) {

        this.reducedMotion.addEventListener(
          'change',
          this.onMotionPreference
        );

      } else if (
        typeof this.reducedMotion
          .addListener === 'function'
      ) {

        this.reducedMotion.addListener(
          this.onMotionPreference
        );

      }


      /*
      Desktop index navigation.
      */

      this.desktopIndexItems.forEach(
        (
          item,
          index
        ) => {

          const handler =
            event => {

              event.preventDefault();

              this.scrollToScene(
                index
              );

            };


          item.__llLifestyleClick =
            handler;


          item.addEventListener(
            'click',
            handler
          );

        }
      );


      /*
      ResizeObserver gives us deterministic geometry
      when Shopify preview/editor changes available size.
      */

      if (
        'ResizeObserver' in window
      ) {

        this.resizeObserver =
          new ResizeObserver(
            () => {

              this.requestGeometry();

            }
          );


        this.resizeObserver.observe(
          this.root
        );


        this.resizeObserver.observe(
          this.desktopStage
        );


        this.resizeObserver.observe(
          this.mobileStage
        );

      }

    }


    onScroll() {

      this.requestRender();

    }


    onResize() {

      this.applyMode();

      this.requestGeometry();

    }


    onPageShow() {

      this.requestGeometry();

    }


    onMotionPreference() {

      this.requestRender();

    }


    /* ======================================================
       GEOMETRY REQUEST
       ====================================================== */

    requestGeometry() {

      if (
        this.geometryTicking ||
        this.destroyed
      ) {
        return;
      }


      this.geometryTicking =
        true;


      window.requestAnimationFrame(
        () => {

          this.geometryTicking =
            false;


          if (this.destroyed) {
            return;
          }


          this.applyMode();

          this.setGeometry();

          this.requestRender();

        }
      );

    }


    /* ======================================================
       GEOMETRY
       ====================================================== */

    setGeometry() {

      if (
        this.destroyed ||
        !this.valid
      ) {
        return;
      }


      const transitions =
        Math.max(
          this.total - 1,
          0
        );


      /* ----------------------------------------------------
         DESKTOP
         Preserve V3 desktop geometry.
         ---------------------------------------------------- */

      if (
        this.mode === 'desktop'
      ) {

        const step =
          this.getRootNumber(
            '--ll-desktop-scroll-step',
            90
          );


        const viewportHeight =
          Math.max(
            window.innerHeight,
            document.documentElement.clientHeight,
            1
          );


        const perTransition =
          viewportHeight *
          (
            step /
            100
          );


        const stageHeight =
          Math.max(
            this.desktopStage.offsetHeight,
            1
          );


        const totalHeight =
          stageHeight +
          (
            perTransition *
            transitions
          );


        /*
        Inline min-height overrides the CSS fallback and keeps
        desktop identical to the previous deterministic engine.
        */

        this.desktopTrack.style.height =
          `${totalHeight}px`;

        this.desktopTrack.style.minHeight =
          `${totalHeight}px`;


        return;
      }


      /* ----------------------------------------------------
         MOBILE
         Independent stable geometry.

         IMPORTANT:
         We intentionally derive scroll distance from the
         actual sticky stage height, NOT window.innerHeight.

         That means browser chrome changes are far less likely
         to alter card timing.
         ---------------------------------------------------- */

      const mobileStep =
        this.getRootNumber(
          '--ll-mobile-scroll-step',
          80
        );


      const stageHeight =
        Math.max(
          this.mobileStage.offsetHeight,
          document.documentElement.clientHeight,
          1
        );


      const perTransition =
        stageHeight *
        (
          mobileStep /
          100
        );


      const totalHeight =
        stageHeight +
        (
          perTransition *
          transitions
        );


      this.mobileTrack.style.height =
        `${totalHeight}px`;

      this.mobileTrack.style.minHeight =
        `${totalHeight}px`;

    }


    /* ======================================================
       RENDER REQUEST
       ====================================================== */

    requestRender() {

      if (
        this.renderTicking ||
        this.destroyed
      ) {
        return;
      }


      this.renderTicking =
        true;


      window.requestAnimationFrame(
        () => {

          this.renderTicking =
            false;


          if (this.destroyed) {
            return;
          }


          this.render();

        }
      );

    }


    /* ======================================================
       MASTER RENDER
       ====================================================== */

    render() {

      this.applyMode();


      if (
        this.mode === 'mobile'
      ) {

        this.renderMobile();

      } else {

        this.renderDesktop();

      }

    }


    /* ======================================================
       DESKTOP ENGINE
       Vertical incoming cards.
       ====================================================== */

    renderDesktop() {

      const rect =
        this.desktopTrack
          .getBoundingClientRect();


      const stickyTop =
        this.getRootNumber(
          '--ll-desktop-sticky-top',
          24
        );


      const totalTravel =
        Math.max(
          1,
          this.desktopTrack.offsetHeight -
          this.desktopStage.offsetHeight
        );


      const travelled =
        stickyTop -
        rect.top;


      const globalProgress =
        clamp(
          travelled /
          totalTravel
        );


      const transitionCount =
        Math.max(
          this.total - 1,
          1
        );


      let current =
        0;


      this.desktopScenes.forEach(
        (
          scene,
          index
        ) => {


          if (
            index === 0
          ) {

            scene.style.setProperty(
              '--ll-desktop-card-y',
              '0%'
            );

            return;
          }


          const segmentStart =
            (
              index - 1
            ) /
            transitionCount;


          const segmentEnd =
            index /
            transitionCount;


          const localProgress =
            clamp(
              (
                globalProgress -
                segmentStart
              )
              /
              (
                segmentEnd -
                segmentStart
              )
            );


          const visualProgress =
            this.reducedMotion.matches

              ? (
                  localProgress >= .5
                    ? 1
                    : 0
                )

              : editorialEase(
                  localProgress
                );


          const translateY =
            (
              1 -
              visualProgress
            ) *
            108;


          scene.style.setProperty(
            '--ll-desktop-card-y',
            `${translateY}%`
          );


          /*
          Preserve previous V3 active timing.
          */

          if (
            localProgress >= .5
          ) {

            current =
              index;

          }

        }
      );


      this.setDesktopActive(
        current
      );


      /*
      Desktop progress remains scene-based, matching
      the previous visual language.
      */

      const progress =
        this.total > 0
          ? (
              current + 1
            ) /
            this.total
          : 1;


      this.root.style.setProperty(
        '--ll-desktop-progress',
        String(progress)
      );

    }


    /* ======================================================
       MOBILE ENGINE
       --------------------------------------------------------
       Vertical scroll controls horizontal cards.

       Card 01 = base scene.
       Card 02 enters from right.
       Card 03 enters from right.

       Previous card receives subtle -4% drift + 1.5% scale
       reduction while being covered.
       ====================================================== */

    renderMobile() {

      const rect =
        this.mobileTrack
          .getBoundingClientRect();


      const stickyTop =
        this.getRootNumber(
          '--ll-mobile-sticky-top',
          0
        );


      const totalTravel =
        Math.max(
          1,
          this.mobileTrack.offsetHeight -
          this.mobileStage.offsetHeight
        );


      const travelled =
        stickyTop -
        rect.top;


      const globalProgress =
        clamp(
          travelled /
          totalTravel
        );


      const transitionCount =
        Math.max(
          this.total - 1,
          1
        );


      /*
      Continuous global progress bar.
      */

      this.root.style.setProperty(
        '--ll-mobile-progress',
        String(globalProgress)
      );


      /*
      PASS 1
      --------------------------------------------------------
      Establish each card's incoming horizontal position.
      */

      this.mobileScenes.forEach(
        (
          scene,
          index
        ) => {


          scene.style.setProperty(
            '--ll-mobile-card-scale',
            '1'
          );


          if (
            index === 0
          ) {

            scene.style.setProperty(
              '--ll-mobile-card-x',
              '0%'
            );

            return;
          }


          const segmentStart =
            (
              index - 1
            ) /
            transitionCount;


          const segmentEnd =
            index /
            transitionCount;


          const localProgress =
            clamp(
              (
                globalProgress -
                segmentStart
              )
              /
              (
                segmentEnd -
                segmentStart
              )
            );


          const visualProgress =
            this.reducedMotion.matches

              ? (
                  localProgress >= .5
                    ? 1
                    : 0
                )

              : editorialEase(
                  localProgress
                );


          const translateX =
            (
              1 -
              visualProgress
            ) *
            104;


          scene.style.setProperty(
            '--ll-mobile-card-x',
            `${translateX}%`
          );

        }
      );


      /*
      PASS 2
      --------------------------------------------------------
      Add extremely restrained depth to the card underneath.

      Incoming:
      104% -> 0%

      Previous:
      0% -> -4%
      scale 1 -> .985
      */

      for (
        let index = 1;
        index < this.total;
        index += 1
      ) {

        const segmentStart =
          (
            index - 1
          ) /
          transitionCount;


        const segmentEnd =
          index /
          transitionCount;


        const localProgress =
          clamp(
            (
              globalProgress -
              segmentStart
            )
            /
            (
              segmentEnd -
              segmentStart
            )
          );


        const visualProgress =
          this.reducedMotion.matches

            ? (
                localProgress >= .5
                  ? 1
                  : 0
              )

            : editorialEase(
                localProgress
              );


        if (
          visualProgress <= 0
        ) {
          continue;
        }


        const previousScene =
          this.mobileScenes[
            index - 1
          ];


        if (!previousScene) {
          continue;
        }


        const previousX =
          -4 *
          visualProgress;


        const previousScale =
          1 -
          (
            .015 *
            visualProgress
          );


        previousScene.style.setProperty(
          '--ll-mobile-card-x',
          `${previousX}%`
        );


        previousScene.style.setProperty(
          '--ll-mobile-card-scale',
          String(previousScale)
        );

      }


      /*
      ACTIVE SCENE
      --------------------------------------------------------
      Incoming scene becomes interactive shortly after
      beginning its entrance.

      This prevents taps from passing through the visible
      incoming card to the card underneath.
      */

      let current =
        0;


      for (
        let index = 1;
        index < this.total;
        index += 1
      ) {

        const segmentStart =
          (
            index - 1
          ) /
          transitionCount;


        const segmentEnd =
          index /
          transitionCount;


        const localProgress =
          clamp(
            (
              globalProgress -
              segmentStart
            )
            /
            (
              segmentEnd -
              segmentStart
            )
          );


        if (
          localProgress >= .10
        ) {

          current =
            index;

        }

      }


      this.setMobileActive(
        current
      );

    }


    /* ======================================================
       DESKTOP ACTIVE STATE
       ====================================================== */

    setDesktopActive(
      index,
      force = false
    ) {

      const next =
        Math.max(
          0,
          Math.min(
            this.total - 1,
            index
          )
        );


      if (
        !force &&
        next === this.activeDesktop
      ) {
        return;
      }


      this.activeDesktop =
        next;


      this.desktopScenes.forEach(
        (
          scene,
          sceneIndex
        ) => {

          const active =
            sceneIndex === next;


          scene.classList.toggle(
            'is-active',
            active
          );


          scene.setAttribute(
            'aria-hidden',
            active
              ? 'false'
              : 'true'
          );


          if (supportsInert) {

            scene.inert =
              !active;

          }

        }
      );


      this.desktopIndexItems.forEach(
        (
          item,
          itemIndex
        ) => {

          const active =
            itemIndex === next;


          item.classList.toggle(
            'is-active',
            active
          );


          if (active) {

            item.setAttribute(
              'aria-current',
              'true'
            );

          } else {

            item.removeAttribute(
              'aria-current'
            );

          }

        }
      );


      if (
        this.desktopCounter
      ) {

        this.desktopCounter.textContent =
          `${padNumber(next + 1)} / ${padNumber(this.total)}`;

      }

    }


    /* ======================================================
       MOBILE ACTIVE STATE
       ====================================================== */

    setMobileActive(
      index,
      force = false
    ) {

      const next =
        Math.max(
          0,
          Math.min(
            this.mobileScenes.length - 1,
            index
          )
        );


      if (
        !force &&
        next === this.activeMobile
      ) {
        return;
      }


      this.activeMobile =
        next;


      this.mobileScenes.forEach(
        (
          scene,
          sceneIndex
        ) => {

          const active =
            sceneIndex === next;


          scene.classList.toggle(
            'is-active',
            active
          );


          scene.setAttribute(
            'aria-hidden',
            active
              ? 'false'
              : 'true'
          );


          if (supportsInert) {

            scene.inert =
              !active;

          }

        }
      );

    }


    /* ======================================================
       SCROLL TO SCENE
       Used by desktop index + Shopify Theme Editor.
       ====================================================== */

    scrollToScene(index) {

      const boundedIndex =
        Math.max(
          0,
          Math.min(
            this.total - 1,
            index
          )
        );


      const progress =
        this.total <= 1

          ? 0

          : boundedIndex /
            (
              this.total - 1
            );


      let target =
        0;


      /* ----------------------------------------------------
         MOBILE
         ---------------------------------------------------- */

      if (
        this.mode === 'mobile'
      ) {

        const stickyTop =
          this.getRootNumber(
            '--ll-mobile-sticky-top',
            0
          );


        const travel =
          Math.max(
            0,
            this.mobileTrack.offsetHeight -
            this.mobileStage.offsetHeight
          );


        target =
          absoluteTop(
            this.mobileTrack
          )
          -
          stickyTop
          +
          (
            travel *
            progress
          );

      }


      /* ----------------------------------------------------
         DESKTOP
         ---------------------------------------------------- */

      else {

        const stickyTop =
          this.getRootNumber(
            '--ll-desktop-sticky-top',
            24
          );


        const travel =
          Math.max(
            0,
            this.desktopTrack.offsetHeight -
            this.desktopStage.offsetHeight
          );


        target =
          absoluteTop(
            this.desktopTrack
          )
          -
          stickyTop
          +
          (
            travel *
            progress
          );

      }


      window.scrollTo({

        top:
          target,

        behavior:
          this.reducedMotion.matches
            ? 'auto'
            : 'smooth'

      });

    }


    /* ======================================================
       SHOPIFY BLOCK SELECTION
       ====================================================== */

    onBlockSelect(event) {

      if (
        String(
          event.detail?.sectionId || ''
        )
        !==
        String(this.sectionId)
      ) {
        return;
      }


      const blockId =
        event.detail?.blockId;


      if (!blockId) {
        return;
      }


      let index =
        this.desktopScenes.findIndex(
          scene => {

            return (
              scene.dataset.blockId
              ===
              blockId
            );

          }
        );


      if (
        index < 0
      ) {

        index =
          this.mobileScenes.findIndex(
            scene => {

              return (
                scene.dataset.blockId
                ===
                blockId
              );

            }
          );

      }


      if (
        index < 0
      ) {
        return;
      }


      this.setGeometry();


      window.requestAnimationFrame(
        () => {

          this.scrollToScene(
            index
          );

        }
      );

    }


    /* ======================================================
       DESTROY
       ====================================================== */

    destroy() {

      if (
        this.destroyed
      ) {
        return;
      }


      this.destroyed =
        true;


      window.removeEventListener(
        'scroll',
        this.onScroll
      );


      window.removeEventListener(
        'resize',
        this.onResize
      );


      window.removeEventListener(
        'orientationchange',
        this.onResize
      );


      window.removeEventListener(
        'pageshow',
        this.onPageShow
      );


      document.removeEventListener(
        'shopify:block:select',
        this.onBlockSelect
      );


      if (
        typeof this.reducedMotion
          .removeEventListener === 'function'
      ) {

        this.reducedMotion.removeEventListener(
          'change',
          this.onMotionPreference
        );

      } else if (
        typeof this.reducedMotion
          .removeListener === 'function'
      ) {

        this.reducedMotion.removeListener(
          this.onMotionPreference
        );

      }


      this.desktopIndexItems.forEach(
        item => {

          if (
            item.__llLifestyleClick
          ) {

            item.removeEventListener(
              'click',
              item.__llLifestyleClick
            );


            delete item.__llLifestyleClick;

          }

        }
      );


      if (
        this.resizeObserver
      ) {

        this.resizeObserver.disconnect();

      }


      delete this.root
        .dataset
        .llLifestyleV4Initialized;


      controllerMap.delete(
        this.root
      );


      controllers.delete(
        this
      );

    }

  }


  /* ========================================================
     INITIALIZATION
     ======================================================== */

  const getRoots = scope => {

    const roots = [];


    if (
      scope?.matches?.(
        '[data-ll-lifestyle-v4]'
      )
    ) {

      roots.push(
        scope
      );

    }


    if (
      scope?.querySelectorAll
    ) {

      roots.push(
        ...scope.querySelectorAll(
          '[data-ll-lifestyle-v4]'
        )
      );

    }


    return roots;

  };


  const initRoot = root => {

    if (
      !root ||
      controllerMap.has(root)
    ) {
      return;
    }


    if (
      root.dataset
        .llLifestyleV4Initialized
      ===
      'true'
    ) {
      return;
    }


    const controller =
      new LifestyleEditsController(
        root
      );


    if (
      !controller.valid
    ) {
      return;
    }


    root.dataset
      .llLifestyleV4Initialized =
      'true';


    controllerMap.set(
      root,
      controller
    );


    controllers.add(
      controller
    );

  };


  const initWithin = scope => {

    getRoots(scope)
      .forEach(
        initRoot
      );

  };


  const initAll = () => {

    initWithin(
      document
    );

  };


  /* ========================================================
     SAFE BOOT
     --------------------------------------------------------
     The Liquid currently loads this asset before its inline
     <style>. Waiting until parsing/layout is complete prevents
     incorrect first measurements.
     ======================================================== */

  const scheduleInit = () => {

    if (
      document.readyState === 'loading'
    ) {

      document.addEventListener(
        'DOMContentLoaded',
        initAll,
        {
          once: true
        }
      );


      return;

    }


    window.requestAnimationFrame(
      () => {

        window.requestAnimationFrame(
          initAll
        );

      }
    );

  };


  /* ========================================================
     SHOPIFY SECTION EVENTS
     ======================================================== */

  document.addEventListener(
    'shopify:section:load',
    event => {

      window.requestAnimationFrame(
        () => {

          initWithin(
            event.target
          );

        }
      );

    }
  );


  document.addEventListener(
    'shopify:section:unload',
    event => {

      const roots =
        getRoots(
          event.target
        );


      roots.forEach(
        root => {

          const controller =
            controllerMap.get(
              root
            );


          if (controller) {

            controller.destroy();

          }

        }
      );

    }
  );


  /* ========================================================
     GLOBAL API
     ======================================================== */

  window[GLOBAL_KEY] = {

    initAll,
    initWithin,
    scheduleInit

  };


  scheduleInit();


})();